// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/DavForm.tsx
//  Página de lançamento / edição do DAV — inspirada no frm_dav do desktop
// ════════════════════════════════════════════════════════════════════════════

import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, Package, CreditCard,
  User, Truck, ClipboardList, CheckCircle2, Undo2, XCircle,
  Printer, FileText, Receipt,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { davService }                from '@/features/operacional/services/davService';
import { useFormasPagamentoCombo, useCondicoesPagamentoCombo } from '@/features/financeiro/hooks/useCadastrosFinanceiros';
import { useConfigSistema }          from '@/features/configuracoes/hooks/useConfiguracoes';
import { useVeiculosCombo }          from '@/features/veiculos/hooks/useVeiculos';
import { useRotaCombo }              from '@/features/rotas/hooks/useRotas';
import { useSalespeople }            from '@/features/pessoas/hooks/useSalespeople';
import { PessoaSearchCombobox }      from '@/components/PessoaSearchCombobox';
import { VendedorSearchCombobox }    from '@/components/VendedorSearchCombobox';
import { getApiErrorMessage }        from '@/lib/apiClient';

import { Button }                    from '@/components/ui/button';
import { Input }                     from '@/components/ui/input';
import { Label }                     from '@/components/ui/label';
import { Textarea }                  from '@/components/ui/textarea';
import { Badge }                     from '@/components/ui/badge';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { apiClient } from '@/lib/apiClient';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';

import type { CriarDavRequest, AtualizarDavRequest, DavProdutoRequest, DavParcelaRequest, DavEntregaRequest } from '@/features/operacional/types';
import { useAuth } from '@/hooks/useAuth';

const BRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ── Tipos internos ────────────────────────────────────────────────────────

interface ItemProduto {
  key: number;
  produtoId: number | '';
  termoBusca: string;
  qtdMovimento: number;
  valorUnitario: number;
  valorDesconto: number;
  valorAcrescimo: number;
  vendedorId: number | '';
}

/** Vendedor para o Select por item. */
interface VendedorOption { id: number; nomeRazao: string; }

interface ParcelaPagamento {
  numero: number;
  formaPagRecId: number | '';
  dtVencimento: string;
  valor: number;
}

const ITEM_VAZIO = (key: number): ItemProduto => ({
  key,
  produtoId: '',
  termoBusca: '',
  qtdMovimento: 1,
  valorUnitario: 0,
  valorDesconto: 0,
  valorAcrescimo: 0,
  vendedorId: '',
});

let _keySeq = 1;
const nextKey = () => ++_keySeq;

// ── Componente: linha de produto com busca ────────────────────────────────

interface ProdutoLinhaProps {
  item: ItemProduto;
  onChange: (campo: keyof ItemProduto, valor: string | number) => void;
  onRemove: () => void;
  showRemove: boolean;
  vendedores: VendedorOption[];
}

const ProdutoLinha = ({ item, onChange, onRemove, showRemove, vendedores }: ProdutoLinhaProps) => {

  const subtotal =
    item.produtoId !== '' && item.qtdMovimento > 0
      ? item.qtdMovimento * item.valorUnitario
        - item.qtdMovimento * item.valorDesconto
        + item.qtdMovimento * item.valorAcrescimo
      : 0;

  return (
    <TableRow>
      {/* Produto */}
      <TableCell className="min-w-[200px]">
        <ProdutoSearchCombobox
          value={item.produtoId === '' ? undefined : item.produtoId}
          descricaoInicial={item.termoBusca || null}
          mostrarPreco
          placeholder="Buscar produto (código, nome ou EAN)..."
          onChange={() => {}}
          onSelectProduto={(p) => {
            onChange('produtoId', p.id);
            onChange('termoBusca', p.descricao);
            onChange('valorUnitario', p.precoPadrao ?? 0);
          }}
        />
      </TableCell>

      {/* Qtd */}
      <TableCell className="w-24">
        <Input
          type="number" min={0.001} step={0.001}
          value={item.qtdMovimento}
          onChange={(e) => onChange('qtdMovimento', Number(e.target.value))}
          className="h-8 text-xs text-right"
        />
      </TableCell>

      {/* Vlr Unit. */}
      <TableCell className="w-28">
        <Input
          type="number" min={0} step={0.01}
          value={item.valorUnitario}
          onChange={(e) => onChange('valorUnitario', Number(e.target.value))}
          className="h-8 text-xs text-right"
        />
      </TableCell>

      {/* Desconto */}
      <TableCell className="w-28">
        <Input
          type="number" min={0} step={0.01}
          value={item.valorDesconto}
          onChange={(e) => onChange('valorDesconto', Number(e.target.value))}
          className="h-8 text-xs text-right"
        />
      </TableCell>

      {/* Acréscimo */}
      <TableCell className="w-28">
        <Input
          type="number" min={0} step={0.01}
          value={item.valorAcrescimo}
          onChange={(e) => onChange('valorAcrescimo', Number(e.target.value))}
          className="h-8 text-xs text-right"
        />
      </TableCell>

      {/* Vendedor do item */}
      <TableCell className="w-40">
        <Select
          value={item.vendedorId !== '' ? String(item.vendedorId) : '__none__'}
          onValueChange={(v) => onChange('vendedorId', v === '__none__' ? '' : Number(v))}
        >
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— Padrão —</SelectItem>
            {vendedores.map((v) => (
              <SelectItem key={v.id} value={String(v.id)}>{v.nomeRazao}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>

      {/* Subtotal */}
      <TableCell className="w-28 text-right text-xs font-medium">
        {subtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
      </TableCell>

      {/* Remover */}
      <TableCell className="w-10">
        {showRemove && (
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={onRemove}>
            <Trash2 className="h-3 w-3 text-destructive" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

// ── Página principal ──────────────────────────────────────────────────────

const DavForm = () => {
  const navigate    = useNavigate();
  const { id }      = useParams<{ id: string }>();
  const { user }    = useAuth();
  const queryClient = useQueryClient();
  const isEdicao    = !!id;

  // ── Carrega DAV para edição ────────────────────────────────────────────
  const { data: davExistente, isLoading: isLoadingDav } = useQuery({
    queryKey: ['dav', id],
    queryFn: () => davService.getById(Number(id)),
    enabled: isEdicao,
  });

  // ── Combos ─────────────────────────────────────────────────────────────
  const { formasPagamento } = useFormasPagamentoCombo();
  const { condicoes: condicoesPagamento } = useCondicoesPagamentoCombo();
  const { data: veiculos = [] } = useVeiculosCombo();
  const { rotas }               = useRotaCombo();
  const { salespeople }         = useSalespeople();

  // ── Estado: Identificação ─────────────────────────────────────────────
  const [vendedorId,    setVendedorId]    = useState<number | undefined>(undefined);
  const [vendedor2Id,   setVendedor2Id]   = useState<number | undefined>(undefined);
  const [perComissao,   setPerComissao]   = useState(0);
  const [perComissao2,  setPerComissao2]  = useState(0);
  const [clienteId,     setClienteId]     = useState<number | undefined>(undefined);
  const [nomeRazao,     setNomeRazao]     = useState('');
  const [cpfCnpj,       setCpfCnpj]       = useState('');
  const [fone,          setFone]          = useState('');
  const [celular,       setCelular]       = useState('');
  const [dtEmissao,     setDtEmissao]     = useState(() => new Date().toISOString().split('T')[0]);
  const [tipoEntrega,   setTipoEntrega]   = useState<'retirada' | 'entrega'>('retirada');
  const [dtEntrega,     setDtEntrega]     = useState('');
  const [horaEntrega,   setHoraEntrega]   = useState('');
  // Endereço de entrega (espelha frm_dav desktop)
  const [entEndereco,    setEntEndereco]    = useState('');
  const [entNumero,      setEntNumero]      = useState('');
  const [entBairro,      setEntBairro]      = useState('');
  const [entComplemento, setEntComplemento] = useState('');
  const [entCep,         setEntCep]         = useState('');
  const [entCidade,      setEntCidade]      = useState('');
  const [entUf,          setEntUf]          = useState('');
  const [entVeiculoId,   setEntVeiculoId]   = useState<number | ''>('');
  const [entRotaId,      setEntRotaId]      = useState<number | ''>('');
  const [entObservacao,  setEntObservacao]  = useState('');

  // ── Estado: Produtos ──────────────────────────────────────────────────
  const [itens, setItens] = useState<ItemProduto[]>([ITEM_VAZIO(nextKey())]);

  // ── Estado: Financeiro ────────────────────────────────────────────────
  const [formaPagRecId,  setFormaPagRecId]  = useState<number | ''>('');
  const [condicaoPagamentoId, setCondicaoPagamentoId] = useState<number | ''>('');
  const [qtdParcelas,    setQtdParcelas]    = useState(1);
  const [diasParcela,    setDiasParcela]    = useState(30);
  const [dtPrimeiroVenc, setDtPrimeiroVenc] = useState(() => new Date().toISOString().split('T')[0]);
  const [descontoGeral,  setDescontoGeral]  = useState(0);
  const [acrescimoGeral, setAcrescimoGeral] = useState(0);
  const [parcelas,       setParcelas]       = useState<ParcelaPagamento[]>([]);

  // ── Estado: Observação ────────────────────────────────────────────────
  const [observacao, setObservacao] = useState('');

  // ── Preenche ao editar ────────────────────────────────────────────────
  useEffect(() => {
    if (!davExistente) return;
    setVendedorId(davExistente.vendedorId);
    setVendedor2Id(davExistente.vendedor2Id);
    setPerComissao(davExistente.perComissaoVendedor ?? 0);
    setPerComissao2(davExistente.perComissaoVendedor2 ?? 0);
    setClienteId(davExistente.clienteId);
    setNomeRazao(davExistente.nomeRazao ?? '');
    setCpfCnpj(davExistente.cpfCnpj ?? '');
    setFone(davExistente.fone ?? '');
    setCelular(davExistente.celular ?? '');
    setDtEmissao(davExistente.dtEmissao.split('T')[0]);
    setObservacao(davExistente.observacao ?? '');
    setFormaPagRecId(davExistente.formaPagRecId ?? '');
    setCondicaoPagamentoId(davExistente.condicaoPagamentoId ?? '');
    setQtdParcelas(davExistente.parcela ?? 1);
    setDiasParcela(davExistente.diasParcela ?? 30);
    // Carrega os dados de entrega (endereço + agendamento) se houver registro.
    if (davExistente.entrega) {
      const e = davExistente.entrega;
      setTipoEntrega('entrega');
      setEntEndereco(e.endereco ?? '');
      setEntNumero(e.numero ?? '');
      setEntBairro(e.bairro ?? '');
      setEntComplemento(e.complemento ?? '');
      setEntCep(e.cep ?? '');
      setEntCidade(e.cidade ?? '');
      setEntUf(e.uf ?? '');
      setEntVeiculoId(e.veiculoId ?? '');
      setEntRotaId(e.rotaId ?? '');
      setEntObservacao(e.observacao ?? '');
      setDtEntrega((e.dtEntrega ?? '').split('T')[0]);
      setHoraEntrega(e.horaEntrega ?? '');
    }
    // Rascunho (não baixado/estornado): carrega os itens p/ edição. ValorDesconto/
    // Acrescimo são armazenados por LINHA (qtd × unitário) → converte de volta p/ unitário.
    if (!davExistente.isBaixada && !davExistente.isEstornado && davExistente.produtos.length > 0) {
      setItens(
        davExistente.produtos.map((p) => ({
          key:            nextKey(),
          produtoId:      p.produtoId,
          termoBusca:     p.produtoDescricao ?? '',
          qtdMovimento:   p.qtdMovimento,
          valorUnitario:  p.valorUnitario,
          valorDesconto:  p.qtdMovimento > 0 ? p.valorDesconto  / p.qtdMovimento : 0,
          valorAcrescimo: p.qtdMovimento > 0 ? p.valorAcrescimo / p.qtdMovimento : 0,
          vendedorId:     p.vendedorId ?? '',
        })),
      );
      // Desconto/acréscimo GERAL implícito = total persistido − soma dos itens.
      const somaDescItens = davExistente.produtos.reduce((s, p) => s + p.valorDesconto, 0);
      const somaAcreItens = davExistente.produtos.reduce((s, p) => s + p.valorAcrescimo, 0);
      setDescontoGeral(Math.max(0, (davExistente.totalDesconto ?? 0) - somaDescItens));
      setAcrescimoGeral(Math.max(0, (davExistente.totalAcrescimo ?? 0) - somaAcreItens));
    }
    // Carrega as parcelas financeiras já gravadas (MovDavFinanceiro) para exibição/edição.
    if (davExistente.financeiros && davExistente.financeiros.length > 0) {
      setParcelas(
        davExistente.financeiros
          .slice()
          .sort((a, b) => a.nroParcela - b.nroParcela)
          .map((f) => ({
            numero:        f.nroParcela,
            formaPagRecId: f.formaPagRecId ?? '',
            dtVencimento:  (f.dtVencimento ?? '').split('T')[0],
            valor:         f.valorReceber,
          })),
      );
    }
  }, [davExistente]);

  // ── Totais ────────────────────────────────────────────────────────────

  const totalBruto = itens.reduce(
    (s, p) =>
      s + (p.produtoId !== '' && p.qtdMovimento > 0
        ? p.qtdMovimento * p.valorUnitario
        : 0),
    0,
  );
  const totalDescItens = itens.reduce(
    (s, p) =>
      s + (p.produtoId !== '' && p.qtdMovimento > 0
        ? p.qtdMovimento * p.valorDesconto
        : 0),
    0,
  );
  const totalAcrescItens = itens.reduce(
    (s, p) =>
      s + (p.produtoId !== '' && p.qtdMovimento > 0
        ? p.qtdMovimento * p.valorAcrescimo
        : 0),
    0,
  );
  const totalLiquido =
    totalBruto - totalDescItens + totalAcrescItens - descontoGeral + acrescimoGeral;

  // Na edição os itens não são recarregados (totalLiquido=0); usa o total persistido
  // para que "Gerar Parcelas" não zere o financeiro já gravado.
  const totalLiquidoFinanceiro =
    isEdicao && totalLiquido <= 0 ? (davExistente?.totalLiquido ?? 0) : totalLiquido;

  // Limite de compra e saldo do cliente (frm_dav "R$ Limite Compra"): aviso, não bloqueia.
  const { data: credito } = useQuery({
    queryKey: ['dav-credito-cliente', clienteId],
    queryFn: () => davService.getCreditoCliente(clienteId!),
    enabled: !!clienteId,
    staleTime: 30_000,
  });

  // Itens só são editáveis em rascunho (criação ou DAV não baixado/não estornado).
  // DAV baixado/faturado tem itens congelados (já movimentaram estoque ou viraram nota).
  const itensEditaveis =
    !isEdicao || (!!davExistente && !davExistente.isBaixada && !davExistente.isEstornado && !davExistente.isImportadoNf);

  // ── Geração de parcelas ───────────────────────────────────────────────

  const gerarParcelas = () => {
    if (totalLiquidoFinanceiro <= 0 || qtdParcelas < 1) return;
    const vlrParcela = totalLiquidoFinanceiro / qtdParcelas;
    const novas: ParcelaPagamento[] = Array.from({ length: qtdParcelas }, (_, i) => {
      const dt = new Date(dtPrimeiroVenc);
      dt.setDate(dt.getDate() + i * diasParcela);
      return {
        numero:        i + 1,
        formaPagRecId: formaPagRecId,
        dtVencimento:  dt.toISOString().split('T')[0],
        valor:         Math.round(vlrParcela * 100) / 100,
      };
    });
    setParcelas(novas);
  };

  // ── Mutations ─────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (req: CriarDavRequest) => davService.create(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dav'] });
      toast.success('Pedido de venda criado com sucesso!');
      navigate('/operacional/dav');
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ data }: { data: AtualizarDavRequest }) =>
      davService.update(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dav'] });
      toast.success('Pedido de venda atualizado com sucesso!');
      navigate('/operacional/dav');
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  // ── Baixa / Reabertura / Estorno ───────────────────────────────────────
  // Regras (devem bater com o DavController):
  //   • Baixar: flag false→true (API aciona baixa automática se config=1)
  //   • Reabrir: true→false SÓ se ambas as flags de baixa automática estiverem 0
  //   • Estornar: único caminho quando a config=1 — cria movimentos compensatórios

  const { data: configSistema } = useConfigSistema(user?.empresa?.id ?? 1);
  const baixaEstoqueAtiva    = configSistema?.operacional.iDavBaixaEstoque === 1;
  const baixaFinanceiroAtiva = configSistema?.operacional.iDavBaixaFinanceiro === 1;
  const podeReabrir          = !baixaEstoqueAtiva && !baixaFinanceiroAtiva;

  const [modalEstornoAberto, setModalEstornoAberto] = useState(false);
  const [motivoEstorno, setMotivoEstorno]           = useState('');

  const baixarMutation = useMutation({
    mutationFn: () => davService.update(Number(id), { isBaixada: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dav'] });
      toast.success(
        baixaEstoqueAtiva
          ? 'DAV baixado. Movimento de estoque gerado automaticamente.'
          : 'DAV baixado.'
      );
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const reabrirMutation = useMutation({
    mutationFn: () => davService.update(Number(id), { isBaixada: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dav'] });
      toast.success('DAV reaberto.');
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const estornarMutation = useMutation({
    mutationFn: () => davService.estornar(Number(id), { motivoEstorno }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dav'] });
      setModalEstornoAberto(false);
      setMotivoEstorno('');
      toast.success(
        baixaEstoqueAtiva
          ? 'DAV estornado. Movimento de estoque compensatório gerado.'
          : 'DAV estornado.'
      );
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const isSaving = createMutation.isPending || updateMutation.isPending
    || baixarMutation.isPending || reabrirMutation.isPending || estornarMutation.isPending;

  // ── Impressão ──────────────────────────────────────────────────────────

  const imprimirTermica = (via = 1) => {
    if (!id) return;
    const qs = via >= 2 ? `?via=${via}` : '';
    window.open(`/operacional/dav/${id}/imprimir-termica${qs}`, '_blank');
  };

  const imprimirA4 = async (via = 1) => {
    if (!id) return;
    try {
      const response = await apiClient.get(`/api/dav/${id}/pdf`, {
        responseType: 'blob',
        params: via >= 2 ? { via } : undefined,
      });
      const url = URL.createObjectURL(response.data as Blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────

  const handleSubmit = () => {
    const produtosValidos: DavProdutoRequest[] = itens
      .filter((p) => p.produtoId !== '' && p.qtdMovimento > 0 && p.valorUnitario > 0)
      .map((p) => ({
        produtoId:     Number(p.produtoId),
        subProdutoId:  0,
        qtdMovimento:  p.qtdMovimento,
        valorUnitario: p.valorUnitario,
        valorDesconto: p.valorDesconto  || undefined,
        valorAcrescimo: p.valorAcrescimo || undefined,
        vendedorId:    p.vendedorId !== '' ? Number(p.vendedorId) : undefined,
      }));

    // Entrega: só envia quando o pedido é "Entrega no endereço". Campos vazios → undefined.
    const entregaReq: DavEntregaRequest | undefined =
      tipoEntrega === 'entrega'
        ? {
            endereco:    entEndereco    || undefined,
            numero:      entNumero      || undefined,
            bairro:      entBairro      || undefined,
            complemento: entComplemento || undefined,
            cep:         entCep         || undefined,
            cidade:      entCidade      || undefined,
            uf:          entUf          || undefined,
            veiculoId:   entVeiculoId !== '' ? Number(entVeiculoId) : undefined,
            rotaId:      entRotaId    !== '' ? Number(entRotaId)    : undefined,
            dtEntrega:   dtEntrega   || undefined,
            horaEntrega: horaEntrega || undefined,
            observacao:  entObservacao  || undefined,
          }
        : undefined;

    // Parcelas (override) só são enviadas se coerentes com o total atual; senão o
    // servidor regenera (evita parcelas defasadas após editar itens/descontos).
    const somaParcelas = parcelas.reduce((s, p) => s + p.valor, 0);
    const parcelasReq: DavParcelaRequest[] | undefined =
      parcelas.length > 0 && Math.abs(somaParcelas - totalLiquidoFinanceiro) < 0.01
        ? parcelas.map((p) => ({
            nroParcela:    p.numero,
            dtVencimento:  p.dtVencimento,
            valor:         p.valor,
            formaPagRecId: p.formaPagRecId !== '' ? Number(p.formaPagRecId) : undefined,
          }))
        : undefined;

    if (!isEdicao) {
      if (produtosValidos.length === 0) {
        toast.error('Adicione pelo menos um produto com quantidade e valor.');
        return;
      }

      const req: CriarDavRequest = {
        vendedorId:   vendedorId ?? user?.id ?? 1,
        dtEmissao,
        produtos:     produtosValidos,
        clienteId:    clienteId    || undefined,
        nomeRazao:    nomeRazao    || undefined,
        cpfCnpj:      cpfCnpj      || undefined,
        fone:         fone         || undefined,
        celular:      celular      || undefined,
        observacao:   observacao   || undefined,
        formaPagRecId: formaPagRecId !== '' ? Number(formaPagRecId) : undefined,
        condicaoPagamentoId: condicaoPagamentoId !== '' ? Number(condicaoPagamentoId) : undefined,
        parcela:      qtdParcelas  > 1  ? qtdParcelas : undefined,
        diasParcela:  diasParcela  > 0  ? diasParcela : undefined,
        descontoGeral:  descontoGeral  > 0 ? descontoGeral  : undefined,
        acrescimoGeral: acrescimoGeral > 0 ? acrescimoGeral : undefined,
        vendedor2Id:          vendedor2Id,
        perComissaoVendedor:  perComissao  > 0 ? perComissao  : undefined,
        perComissaoVendedor2: perComissao2 > 0 ? perComissao2 : undefined,
        parcelas:     parcelasReq,
        entrega:      entregaReq,
      };
      createMutation.mutate(req);
    } else {
      if (itensEditaveis && produtosValidos.length === 0) {
        toast.error('Adicione pelo menos um produto com quantidade e valor.');
        return;
      }

      const req: AtualizarDavRequest = {
        clienteId:    clienteId    || undefined,
        nomeRazao:    nomeRazao    || undefined,
        cpfCnpj:      cpfCnpj      || undefined,
        fone:         fone         || undefined,
        celular:      celular      || undefined,
        observacao:   observacao   || undefined,
        formaPagRecId: formaPagRecId !== '' ? Number(formaPagRecId) : undefined,
        condicaoPagamentoId: condicaoPagamentoId !== '' ? Number(condicaoPagamentoId) : undefined,
        parcela:      qtdParcelas,
        diasParcela,
        vendedor2Id:          vendedor2Id,
        perComissaoVendedor:  perComissao,
        perComissaoVendedor2: perComissao2,
        // Itens do rascunho (só envia quando editável) + descontos gerais p/ recalcular.
        produtos:       itensEditaveis ? produtosValidos : undefined,
        descontoGeral:  itensEditaveis ? descontoGeral  : undefined,
        acrescimoGeral: itensEditaveis ? acrescimoGeral : undefined,
        parcelas:     parcelasReq,
        entrega:      entregaReq,
      };
      updateMutation.mutate({ data: req });
    }
  };

  // ── Itens helpers ─────────────────────────────────────────────────────

  const atualizarItem = (key: number, campo: keyof ItemProduto, valor: string | number) =>
    setItens((prev) =>
      prev.map((p) => (p.key === key ? { ...p, [campo]: valor } : p))
    );

  const removerItem = (key: number) =>
    setItens((prev) => prev.filter((p) => p.key !== key));

  const adicionarItem = () =>
    setItens((prev) => [...prev, ITEM_VAZIO(nextKey())]);

  // ── Render ────────────────────────────────────────────────────────────

  if (isEdicao && isLoadingDav) {
    return (
      <div className="p-6 flex items-center justify-center text-muted-foreground">
        Carregando pedido...
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/operacional/dav')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEdicao
                ? `Editar DAV #${davExistente?.numeroDav ?? id}`
                : 'Novo Pedido de Venda (DAV)'}
            </h1>
            <p className="text-muted-foreground text-sm">
              {isEdicao ? 'Altere os dados do pedido' : 'Preencha os dados do novo pedido'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Badges de estado (edição) */}
          {isEdicao && davExistente?.isEstornado && (
            <Badge variant="destructive" className="gap-1">
              <XCircle className="h-3 w-3" /> Estornado
            </Badge>
          )}
          {isEdicao && davExistente?.isBaixada && !davExistente?.isEstornado && (
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3 w-3" /> Baixado
            </Badge>
          )}
          {isEdicao && davExistente?.isImportadoNf && !davExistente?.isEstornado && (
            <Badge variant="secondary" className="gap-1">
              <FileText className="h-3 w-3" /> Importado (NF)
            </Badge>
          )}

          {/* Ações de transição de estado — só na edição e se não estornado */}
          {isEdicao && !davExistente?.isEstornado && (
            <>
              {!davExistente?.isBaixada && (
                <Button
                  variant="default"
                  onClick={() => baixarMutation.mutate()}
                  disabled={isSaving}
                  className="gap-2"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Baixar
                </Button>
              )}
              {davExistente?.isBaixada && podeReabrir && (
                <Button
                  variant="outline"
                  onClick={() => reabrirMutation.mutate()}
                  disabled={isSaving}
                  className="gap-2"
                >
                  <Undo2 className="h-4 w-4" />
                  Reabrir
                </Button>
              )}
              {davExistente?.isBaixada && (
                <Button
                  variant="destructive"
                  onClick={() => setModalEstornoAberto(true)}
                  disabled={isSaving}
                  className="gap-2"
                  title={!podeReabrir ? 'Estornar (reabertura indisponível — baixa automática ativa)' : 'Estornar DAV'}
                >
                  <XCircle className="h-4 w-4" />
                  Estornar
                </Button>
              )}
            </>
          )}

          {isEdicao && id && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Printer className="h-4 w-4" />
                  Imprimir
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => imprimirA4(1)} className="gap-2">
                  <FileText className="h-4 w-4" />
                  Imprimir A4 (PDF)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => imprimirTermica(1)} className="gap-2">
                  <Receipt className="h-4 w-4" />
                  Imprimir Térmica 80mm
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => imprimirA4(2)} className="gap-2">
                  <FileText className="h-4 w-4" />
                  2ª via — A4 (PDF)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => imprimirTermica(2)} className="gap-2">
                  <Receipt className="h-4 w-4" />
                  2ª via — Térmica 80mm
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button variant="outline" onClick={() => navigate('/operacional/dav')}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving || davExistente?.isEstornado}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {isSaving ? 'Salvando...' : isEdicao ? 'Salvar Alterações' : 'Criar Pedido'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="identificacao" className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="identificacao" className="gap-2">
            <User className="h-4 w-4" />
            Identificação
          </TabsTrigger>
          <TabsTrigger value="produtos" className="gap-2">
            <Package className="h-4 w-4" />
            Produtos
          </TabsTrigger>
          <TabsTrigger value="financeiro" className="gap-2">
            <CreditCard className="h-4 w-4" />
            Financeiro
          </TabsTrigger>
          <TabsTrigger value="observacoes" className="gap-2">
            <ClipboardList className="h-4 w-4" />
            Observações
          </TabsTrigger>
        </TabsList>

        {/* ── Tab: Identificação ────────────────────────────────────── */}
        <TabsContent value="identificacao">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Dados do Pedido</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Vendedor + Data */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Data de Emissão *</Label>
                  <Input
                    type="date"
                    value={dtEmissao}
                    onChange={(e) => setDtEmissao(e.target.value)}
                    disabled={isEdicao}
                  />
                </div>

                <div className="space-y-1">
                  <Label>Vendedor</Label>
                  <VendedorSearchCombobox
                    value={vendedorId}
                    onChange={(v) => setVendedorId(v ?? undefined)}
                    placeholder="Selecione o vendedor..."
                    disabled={isEdicao}
                  />
                </div>

                <div className="space-y-1">
                  <Label>Número do DAV</Label>
                  <Input
                    value={isEdicao ? (davExistente?.numeroDav ?? '') : 'Gerado automaticamente'}
                    disabled
                    className="text-muted-foreground"
                  />
                </div>
              </div>

              {/* Segundo vendedor + comissões (split de comissão) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>2º Vendedor</Label>
                  <VendedorSearchCombobox
                    value={vendedor2Id}
                    onChange={(v) => setVendedor2Id(v ?? undefined)}
                    placeholder="Opcional..."
                  />
                </div>

                <div className="space-y-1">
                  <Label>% Comissão Vendedor</Label>
                  <Input
                    type="number" min={0} max={100} step={0.01}
                    value={perComissao}
                    onChange={(e) => setPerComissao(Number(e.target.value))}
                  />
                </div>

                <div className="space-y-1">
                  <Label>% Comissão 2º Vendedor</Label>
                  <Input
                    type="number" min={0} max={100} step={0.01}
                    value={perComissao2}
                    onChange={(e) => setPerComissao2(Number(e.target.value))}
                    disabled={vendedor2Id === undefined}
                  />
                </div>
              </div>

              <Separator />

              {/* Cliente */}
              <div>
                <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
                  Cliente
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Buscar Cliente Cadastrado</Label>
                    <PessoaSearchCombobox
                      value={clienteId}
                      onChange={setClienteId}
                      filter="cliente"
                      placeholder="Buscar cliente..."
                    />
                    {credito && (
                      <div className="flex flex-wrap gap-1.5 pt-1 text-xs">
                        <Badge variant="outline">
                          Limite: {credito.limite > 0 ? BRL(credito.limite) : 'sem limite'}
                        </Badge>
                        <Badge variant="outline">Em aberto: {BRL(credito.emAberto)}</Badge>
                        {credito.vencido > 0 && (
                          <Badge className="bg-red-500/10 text-red-600">Vencido: {BRL(credito.vencido)}</Badge>
                        )}
                        {credito.disponivel !== null && (
                          <Badge className={credito.disponivel < totalLiquidoFinanceiro
                            ? 'bg-red-500/10 text-red-600' : 'bg-green-500/10 text-green-700'}>
                            Disponível: {BRL(credito.disponivel)}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label>CPF / CNPJ</Label>
                    <Input
                      value={cpfCnpj}
                      onChange={(e) => setCpfCnpj(e.target.value)}
                      placeholder="000.000.000-00"
                    />
                  </div>

                  <div className="space-y-1 sm:col-span-2">
                    <Label>Nome / Razão Social (consumidor final)</Label>
                    <Input
                      value={nomeRazao}
                      onChange={(e) => setNomeRazao(e.target.value)}
                      placeholder="Nome do cliente (opcional)"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label>Telefone</Label>
                    <Input
                      value={fone}
                      onChange={(e) => setFone(e.target.value)}
                      placeholder="(00) 0000-0000"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label>Celular</Label>
                    <Input
                      value={celular}
                      onChange={(e) => setCelular(e.target.value)}
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Entrega */}
              <div>
                <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <Truck className="h-4 w-4" />
                  Opção de Entrega
                </h3>
                <RadioGroup
                  value={tipoEntrega}
                  onValueChange={(v) => setTipoEntrega(v as 'retirada' | 'entrega')}
                  className="flex gap-6"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="retirada" id="retirada" />
                    <Label htmlFor="retirada" className="cursor-pointer font-normal">
                      Retirada na loja
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="entrega" id="entrega" />
                    <Label htmlFor="entrega" className="cursor-pointer font-normal">
                      Entrega no endereço
                    </Label>
                  </div>
                </RadioGroup>

                {tipoEntrega === 'entrega' && (
                  <div className="mt-4 space-y-4">
                    {/* Endereço de entrega */}
                    <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                      <div className="space-y-1 sm:col-span-4">
                        <Label>Endereço</Label>
                        <Input
                          value={entEndereco}
                          onChange={(e) => setEntEndereco(e.target.value)}
                          placeholder="Logradouro"
                        />
                      </div>
                      <div className="space-y-1 sm:col-span-1">
                        <Label>Número</Label>
                        <Input value={entNumero} onChange={(e) => setEntNumero(e.target.value)} />
                      </div>
                      <div className="space-y-1 sm:col-span-1">
                        <Label>CEP</Label>
                        <Input value={entCep} onChange={(e) => setEntCep(e.target.value)} />
                      </div>
                      <div className="space-y-1 sm:col-span-2">
                        <Label>Bairro</Label>
                        <Input value={entBairro} onChange={(e) => setEntBairro(e.target.value)} />
                      </div>
                      <div className="space-y-1 sm:col-span-2">
                        <Label>Complemento</Label>
                        <Input value={entComplemento} onChange={(e) => setEntComplemento(e.target.value)} />
                      </div>
                      <div className="space-y-1 sm:col-span-1">
                        <Label>Cidade</Label>
                        <Input value={entCidade} onChange={(e) => setEntCidade(e.target.value)} />
                      </div>
                      <div className="space-y-1 sm:col-span-1">
                        <Label>UF</Label>
                        <Input
                          value={entUf}
                          maxLength={2}
                          onChange={(e) => setEntUf(e.target.value.toUpperCase())}
                        />
                      </div>
                    </div>

                    {/* Veículo / Rota / Agendamento */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-1">
                        <Label>Veículo</Label>
                        <Select
                          value={entVeiculoId !== '' ? String(entVeiculoId) : '__none__'}
                          onValueChange={(v) => setEntVeiculoId(v === '__none__' ? '' : Number(v))}
                        >
                          <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Nenhum —</SelectItem>
                            {veiculos.map((v) => (
                              <SelectItem key={v.id} value={String(v.id)}>
                                {(v.placa ?? `Veículo ${v.id}`)}{v.modelo ? ` — ${v.modelo}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label>Rota</Label>
                        <Select
                          value={entRotaId !== '' ? String(entRotaId) : '__none__'}
                          onValueChange={(v) => setEntRotaId(v === '__none__' ? '' : Number(v))}
                        >
                          <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Nenhuma —</SelectItem>
                            {rotas.map((r) => (
                              <SelectItem key={r.id} value={String(r.id)}>{r.descricao}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label>Data de Entrega</Label>
                        <Input
                          type="date"
                          value={dtEntrega}
                          onChange={(e) => setDtEntrega(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>Hora de Entrega</Label>
                        <Input
                          type="time"
                          value={horaEntrega}
                          onChange={(e) => setHoraEntrega(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label>Observação da Entrega</Label>
                      <Input
                        value={entObservacao}
                        onChange={(e) => setEntObservacao(e.target.value)}
                        placeholder="Ponto de referência, instruções..."
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Produtos ──────────────────────────────────────────── */}
        <TabsContent value="produtos">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Itens do Pedido</CardTitle>
                {itensEditaveis && (
                  <Button type="button" size="sm" variant="outline" onClick={adicionarItem} className="gap-1">
                    <Plus className="h-3 w-3" />
                    Adicionar Item
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {!itensEditaveis ? (
                <p className="text-sm text-muted-foreground">
                  Os itens não podem ser alterados: o pedido já foi baixado/faturado.
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Produto *</TableHead>
                          <TableHead className="w-24 text-right">Qtd *</TableHead>
                          <TableHead className="w-28 text-right">Vlr Unit. *</TableHead>
                          <TableHead className="w-28 text-right">Desconto</TableHead>
                          <TableHead className="w-28 text-right">Acréscimo</TableHead>
                          <TableHead className="w-40">Vendedor</TableHead>
                          <TableHead className="w-28 text-right">Subtotal</TableHead>
                          <TableHead className="w-10" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {itens.map((item) => (
                          <ProdutoLinha
                            key={item.key}
                            item={item}
                            onChange={(campo, valor) => atualizarItem(item.key, campo, valor)}
                            onRemove={() => removerItem(item.key)}
                            showRemove={itens.length > 1}
                            vendedores={salespeople}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Totalizadores */}
                  <div className="flex justify-end">
                    <div className="w-72 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Bruto:</span>
                        <span>
                          {totalBruto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                      </div>
                      <div className="flex justify-between text-red-500">
                        <span>(-) Descontos itens:</span>
                        <span>
                          {totalDescItens.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                      </div>
                      <div className="flex justify-between text-emerald-600">
                        <span>(+) Acréscimos itens:</span>
                        <span>
                          {totalAcrescItens.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                      </div>
                      <Separator />
                      <div className="flex justify-between font-semibold text-base">
                        <span>Total Líquido:</span>
                        <Badge variant="outline" className="text-base px-3">
                          {totalLiquido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Financeiro ────────────────────────────────────────── */}
        <TabsContent value="financeiro">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Condições de Pagamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Condição de pagamento — preenche forma/parcelas/intervalo ao selecionar */}
              <div className="space-y-1 max-w-xl">
                <Label>Condição de Pagamento</Label>
                <Select
                  value={condicaoPagamentoId !== '' ? String(condicaoPagamentoId) : '__none__'}
                  onValueChange={(v) => {
                    if (v === '__none__') { setCondicaoPagamentoId(''); return; }
                    const id = Number(v);
                    setCondicaoPagamentoId(id);
                    const cond = condicoesPagamento.find((c) => c.id === id);
                    if (cond) {
                      if (cond.formaPagRecId) setFormaPagRecId(cond.formaPagRecId);
                      if (cond.quantidadeParcelas && cond.quantidadeParcelas > 0) setQtdParcelas(cond.quantidadeParcelas);
                      if (cond.diasMes != null && cond.diasMes > 0) setDiasParcela(cond.diasMes);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma condição (opcional)..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Nenhuma (informar manualmente) —</SelectItem>
                    {condicoesPagamento.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {(c.descricao ?? `Condição ${c.codigoCondicao}`)} — {c.quantidadeParcelas}x
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Preenche forma de pagamento, nº de parcelas e intervalo automaticamente.
                </p>
              </div>

              {/* Forma de pagamento + parcelas */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-1 sm:col-span-2">
                  <Label>Forma de Pagamento</Label>
                  <Select
                    value={formaPagRecId !== '' ? String(formaPagRecId) : ''}
                    onValueChange={(v) => setFormaPagRecId(v ? Number(v) : '')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {formasPagamento.map((fp) => (
                        <SelectItem key={fp.id} value={String(fp.id)}>
                          {fp.descricao}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label>Nº de Parcelas</Label>
                  <Input
                    type="number" min={1} max={99}
                    value={qtdParcelas}
                    onChange={(e) => setQtdParcelas(Number(e.target.value))}
                  />
                </div>

                <div className="space-y-1">
                  <Label>Dias entre Parcelas</Label>
                  <Input
                    type="number" min={0}
                    value={diasParcela}
                    onChange={(e) => setDiasParcela(Number(e.target.value))}
                  />
                </div>
              </div>

              {/* Desconto geral / Acréscimo geral / 1º vencimento */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Data 1º Vencimento</Label>
                  <Input
                    type="date"
                    value={dtPrimeiroVenc}
                    onChange={(e) => setDtPrimeiroVenc(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <Label>Desconto Geral (R$)</Label>
                  <Input
                    type="number" min={0} step={0.01}
                    value={descontoGeral}
                    onChange={(e) => setDescontoGeral(Number(e.target.value))}
                  />
                </div>

                <div className="space-y-1">
                  <Label>Acréscimo Geral (R$)</Label>
                  <Input
                    type="number" min={0} step={0.01}
                    value={acrescimoGeral}
                    onChange={(e) => setAcrescimoGeral(Number(e.target.value))}
                  />
                </div>
              </div>

              {/* Resumo financeiro + botão gerar parcelas */}
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 p-4 rounded-md bg-muted/40">
                <div className="space-y-1 text-sm">
                  <div className="flex gap-8">
                    <div>
                      <p className="text-muted-foreground">Total dos Produtos</p>
                      <p className="font-semibold text-base">
                        {totalBruto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Valor Líquido Fatura</p>
                      <p className="font-semibold text-base text-emerald-600">
                        {totalLiquido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </p>
                    </div>
                    {qtdParcelas > 1 && totalLiquido > 0 && (
                      <div>
                        <p className="text-muted-foreground">Valor por Parcela</p>
                        <p className="font-semibold text-base">
                          {(totalLiquido / qtdParcelas).toLocaleString('pt-BR', {
                            style: 'currency', currency: 'BRL',
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <Button type="button" variant="outline" onClick={gerarParcelas} className="gap-2">
                  <CreditCard className="h-4 w-4" />
                  Gerar Parcelas
                </Button>
              </div>

              {/* Grid de parcelas geradas */}
              {parcelas.length > 0 && (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Parcela</TableHead>
                        <TableHead>Forma de Pagamento</TableHead>
                        <TableHead>Vencimento</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parcelas.map((p) => (
                        <TableRow key={p.numero}>
                          <TableCell className="font-medium">{p.numero}/{qtdParcelas}</TableCell>
                          <TableCell>
                            <Select
                              value={p.formaPagRecId !== '' ? String(p.formaPagRecId) : ''}
                              onValueChange={(v) =>
                                setParcelas((prev) =>
                                  prev.map((x) =>
                                    x.numero === p.numero
                                      ? { ...x, formaPagRecId: v ? Number(v) : '' }
                                      : x
                                  )
                                )
                              }
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Selecione..." />
                              </SelectTrigger>
                              <SelectContent>
                                {formasPagamento.map((fp) => (
                                  <SelectItem key={fp.id} value={String(fp.id)}>
                                    {fp.descricao}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="date"
                              value={p.dtVencimento}
                              onChange={(e) =>
                                setParcelas((prev) =>
                                  prev.map((x) =>
                                    x.numero === p.numero
                                      ? { ...x, dtVencimento: e.target.value }
                                      : x
                                  )
                                )
                              }
                              className="h-8 w-36"
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {p.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Observações ───────────────────────────────────────── */}
        <TabsContent value="observacoes">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Observações do Pedido</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Informe observações relevantes para o pedido (entrega, preferências do cliente, etc.)..."
                rows={8}
                className="resize-none"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Botões rodapé */}
      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button variant="outline" onClick={() => navigate('/operacional/dav')}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSaving || davExistente?.isEstornado}
          className="gap-2"
        >
          <Save className="h-4 w-4" />
          {isSaving ? 'Salvando...' : isEdicao ? 'Salvar Alterações' : 'Criar Pedido'}
        </Button>
      </div>

      {/* Modal de Estorno */}
      <Dialog open={modalEstornoAberto} onOpenChange={setModalEstornoAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Estornar DAV #{davExistente?.numeroDav}</DialogTitle>
            <DialogDescription>
              {baixaEstoqueAtiva || baixaFinanceiroAtiva
                ? 'Movimentos compensatórios serão gerados automaticamente conforme as configurações ativas. Esta ação é definitiva — um DAV estornado não pode ser reaberto.'
                : 'O DAV será marcado como estornado. Esta ação é definitiva.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="motivo">Motivo do estorno <span className="text-destructive">*</span></Label>
            <Textarea
              id="motivo"
              value={motivoEstorno}
              onChange={(e) => setMotivoEstorno(e.target.value)}
              placeholder="Descreva o motivo (ex: cliente desistiu, erro de digitação, etc.)"
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalEstornoAberto(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => estornarMutation.mutate()}
              disabled={motivoEstorno.trim().length < 3 || estornarMutation.isPending}
              className="gap-2"
            >
              <XCircle className="h-4 w-4" />
              {estornarMutation.isPending ? 'Estornando...' : 'Confirmar Estorno'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DavForm;
